//== reponsive ==

"use client"

import { Button } from "../ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'

interface PaginationProps {
  currentPage: number
  totalPages: number
  pageSize: number
  totalItems: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}

export function Pagination({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
}: PaginationProps) {
  // Handle edge case when there are no items
  const hasNoData = totalItems === 0 || totalPages === 0
  const safeCurrentPage = hasNoData ? 0 : currentPage
  const safeTotalPages = hasNoData ? 0 : totalPages
  
  // Calculate indices, ensuring they don't go negative
  const startIndex = hasNoData ? 0 : (currentPage - 1) * pageSize
  const endIndex = hasNoData ? 0 : Math.min(startIndex + pageSize, totalItems)

  return (
    <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-3 sm:gap-0 border-t bg-white px-4 sm:px-6 py-3 sm:py-4">
      
      {/* Page Size Selector */}
      <div className="flex items-center gap-2 text-sm sm:text-base text-gray-600">
        <span>Page Size:</span>
        <Select
          value={pageSize.toString()}
          onValueChange={(val) => onPageSizeChange(parseInt(val))}
        >
          <SelectTrigger className="w-20 h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="10">10</SelectItem>
            <SelectItem value="25">25</SelectItem>
            <SelectItem value="50">50</SelectItem>
            <SelectItem value="100">100</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Showing items info */}
      <div className="text-sm sm:text-base text-gray-600">
        {hasNoData ? '0 to 0' : `${startIndex + 1} to ${endIndex}`} of {totalItems}
      </div>

      {/* Pagination Controls */}
      <div className="flex flex-wrap sm:flex-nowrap items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(1)}
          disabled={hasNoData || currentPage === 1}
          className="h-8 w-8 p-0"
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={hasNoData || currentPage === 1}
          className="h-8 w-8 p-0"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="flex items-center gap-1 text-sm sm:text-base font-medium px-2">
          Page {safeCurrentPage} of {safeTotalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={hasNoData || currentPage === totalPages}
          className="h-8 w-8 p-0"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(totalPages)}
          disabled={hasNoData || currentPage === totalPages}
          className="h-8 w-8 p-0"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
